'use client';

import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import type { Team } from '@baseball-near-you/prisma';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '@baseball-near-you/ui';
import { ProtectedAuthModal } from '@bb/components/Auth';
import type { ModalTrigger } from '@bb/components/Auth/types';
import { useSpecificTeam } from '@bb/lib/hooks/useSpecificTeam';
import { TeamBackgroundStep } from './TeamBackgroundStep';
import { TeamOnboardingStep } from './TeamOnboardingStep';
import { TeamSelectionStep } from './TeamSelection/TeamSelectionStep';

export enum Step {
  TeamSelection = 'team-selection',
  Background = 'background',
  Onboarding = 'onboarding',
  OnboardingCreate = 'onboarding-create',
}

function TeamSelectionModalTriggerButton(props: {
  triggerNode?: ModalTrigger;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  return props.triggerNode ? (
    props.triggerNode({ open: props.open, setOpen: props.setOpen })
  ) : (
    <div className="flex flex-col items-center">
      <p className="text-left">
        Request access to a team to gain the ability to update the location,
        images, tryout information, staff, age groups and more!
      </p>
      <Button
        onClick={() => {
          props.setOpen(true);
        }}
        type="button"
        variant={'secondary'}
        className="mt-6 justify-self-center border border-gray-300 bg-gray-100/50"
      >
        search for your team
      </Button>
    </div>
  );
}

export function TeamSelectionModal(props: {
  triggerNode?: ModalTrigger;
  selectedTeamSlug?: string | null;
  onSuccess?: () => Promise<void>;
  hideCantFindTeam?: boolean;
  footerNode?: ReactNode;
  noResultsNode?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const { team: specificTeam } = useSpecificTeam(
    props.selectedTeamSlug ?? null
  );

  useEffect(() => {
    // if using this modal with only one specific team (on the team detail page)
    // just set the selected team after fetching
    if (props.selectedTeamSlug && specificTeam) {
      setSelectedTeam(specificTeam);
    }
  }, [specificTeam, props.selectedTeamSlug]);

  const [step, setStep] = useState<Step>(Step.TeamSelection);

  return (
    <ProtectedAuthModal
      modalTrigger={props.triggerNode}
      modal={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <TeamSelectionModalTriggerButton
              open={open}
              setOpen={setOpen}
              triggerNode={props.triggerNode}
            />
          </DialogTrigger>
          <DialogContent aria-describedby="team-selection-modal body">
            {step === Step.TeamSelection ? (
              <TeamSelectionStep
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
                setStep={setStep}
                specificTeam={specificTeam}
                selectedTeamSlug={props.selectedTeamSlug}
                hideCantFindTeam={props.hideCantFindTeam}
                noResultsNode={props.noResultsNode}
              />
            ) : step === Step.Background ? (
              <TeamBackgroundStep
                selectedTeam={selectedTeam}
                setIsOpen={setOpen}
                setStep={setStep}
              />
            ) : step === Step.Onboarding ? (
              <TeamOnboardingStep
                selectedTeam={selectedTeam}
                setStep={setStep}
                onSuccess={props.onSuccess}
                setOpen={setOpen}
              />
            ) : null}
            {!!props.footerNode && step === Step.TeamSelection && (
              <DialogFooter>{props.footerNode}</DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      }
    />
  );
}
